import { Field } from "@sitecore-jss/sitecore-jss-react";
import moment from "moment";
import React, { useMemo, useReducer } from "react";
import { useHistory } from "react-router-dom";

import { updateAddress } from "../../../api/mutation";
import { Policy } from "../../../api/policy";
import { Checkboxes } from "../../../components/InputFields/Checkboxes";
import DatePicker from "../../../components/InputFields/DatePicker";
import { AddressFields } from "../../../components/Shared/personalDetails/AddressFields";
import { allMandatory } from "../../../helpers/allMandatory/allMandatory";
import { ModelState } from "../../../interfaces/modelState";
import { MutationLayout } from "../MutationLayout";
import { AddressMutationModel } from "./AddressMutation.interfaces";
import { addressMutationSitecoreLabels } from "./AddressMutation.labels";
import { addressMutationReducer, initialState } from "./AddressMutation.state";

interface Props {
  fields: Record<string, Field<string> & { url: string }>;
  policyNr: string;
  policy: Policy;
}

export const AddressMutation = ({ fields, policyNr, policy }: Props) => {
  const history = useHistory();
  const minDate = moment().add(1, "days").toDate();
  const maxDate = moment().add(1, "days").add(1, "year").toDate();

  const [{ data, loading, modelState }, dispatch] = useReducer(addressMutationReducer, initialState);

  // create a copy of the object, because the fields try to mutate it
  const copyOfModelState = { ...modelState };

  const handleStartDate = (startDate?: string) => {
    handleUpdateAddressMutation({ startDate });
  };

  const handleChangeSelectedContactIds = (contactIds: string[]) => {
    handleUpdateAddressMutation({ contactIds });
  };

  const handleUpdateAddressMutation = (addressMutation: Partial<AddressMutationModel>) => {
    dispatch({ type: "UPDATE_ADDRESSMUTATION_DATA", payload: addressMutation });
  };

  const setModelState = (newModelState: ModelState) => {
    dispatch({ type: "UPDATE_ADDRESSMUTATION_ERROR", payload: newModelState });
  };

  const handleAddressMutationClick = async () => {
    try {
      dispatch({ type: "UPDATE_ADDRESSMUTATION_FETCH_START" });
      const response = await updateAddress({ ...data, policyNumber: policyNr });

      if (response.modelState) {
        dispatch({
          type: "UPDATE_ADDRESSMUTATION_FETCH_ERROR",
          payload: response.modelState,
        });
      } else {
        // remove the query parameters
        window.history.replaceState(null, "", window.location.pathname);
        history.push(fields.ThankYouPage.url);
      }
    } catch {
      dispatch({
        type: "UPDATE_ADDRESSMUTATION_FETCH_ERROR",
        payload: { global: ["Er is een onverwachte fout opgetreden. Probeer het opnieuw."] },
      });
    }
  };

  const selectedContactIds = data.contactIds ?? [];

  const canSubmitForm = allMandatory(
    data.postcode,
    data.houseNumber,
    data.street,
    data.city,
    data.startDate,
    selectedContactIds.length > 0
  );

  const policyContacts = useMemo(
    () =>
      [...(policy.policyHolders?.policyHolder ?? []), ...(policy.insureds?.insured ?? [])]
        .map((insured) => {
          const { naturalPersonSummary } = insured;

          const name = [
            naturalPersonSummary.initials,
            naturalPersonSummary.prefixName,
            naturalPersonSummary.surName,
            `(${naturalPersonSummary.dateOfBirth})`,
          ]
            .filter(Boolean)
            .join(" ");

          return { value: insured.contactId, label: name };
        })
        .filter((value, index, self) => self.findIndex((v2) => value.value === v2.value) === index),
    [policy]
  );

  return (
    <MutationLayout
      canSubmitForm={canSubmitForm}
      handleSubmit={handleAddressMutationClick}
      loading={loading}
      modelState={copyOfModelState}
      title="Adres wijzigen"
      CardContent={
        <>
          <Checkboxes
            fieldData={addressMutationSitecoreLabels}
            name="Contacts"
            inputValue={selectedContactIds}
            id="Contacts"
            propertyName="Contacts"
            orientation="vertical"
            options={policyContacts}
            onChange={handleChangeSelectedContactIds}
          />
          <AddressFields
            fields={addressMutationSitecoreLabels}
            data={data}
            modelState={copyOfModelState}
            setModelState={setModelState}
            partialFieldName=""
            onUpdateAddress={handleUpdateAddressMutation}
          />
          <div className="form-row form-row--flex">
            <DatePicker
              required
              fieldData={addressMutationSitecoreLabels}
              name="StartDate"
              minDate={minDate}
              maxDate={maxDate}
              modelState={copyOfModelState}
              setModelState={setModelState}
              onChange={handleStartDate}
              inputValue={data.startDate}
              propertyName="model.StartDate"
            />
          </div>
        </>
      }
      InfoContent={
        <>
          <h6>Verwerkingstermijn</h6>
          <p>
            Het gewijzigde adres is niet direct zichtbaar in het DELAportaal. De verwerkingstermijn bedraagt maximaal 2
            werkdagen. Er wordt <strong>geen</strong> nieuwe polis afgegeven.
          </p>

          <h6>Adres in het buitenland</h6>
          <p>
            Een wijziging naar een adres in het buitenland kun je hier niet doorgeven. Annuleer deze wijziging en kies
            onder ‘Polis wijzigen’ voor ‘Overig’. Geef hier de wijziging naar een adres in het buitenland door.
          </p>
        </>
      }
    />
  );
};
